import React from 'react';
import { View, TouchableOpacity, StyleSheet, Image, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { linkedAccountsStrings } from '@/constants/residents/strings';
import { LinkedAccount } from '@/types/residents/home';
import { GlassCard } from '../redesign/GlassCard';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface LinkedAccountsCardProps {
  linkedAccounts: LinkedAccount[];
  onPress: () => void;
  testID?: string;
  style?: StyleProp<ViewStyle>;
}

export const LinkedAccountsCard: React.FC<LinkedAccountsCardProps> = ({
  linkedAccounts,
  onPress,
  testID,
  style,
}) => {
  const { colors } = useThemeColors();
  const visibleAccounts = linkedAccounts.length > 4 ? linkedAccounts.slice(0, 4) : linkedAccounts;
  const hasMoreThanFour = linkedAccounts.length > 4;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={'ProfileCell_profile.option.connections'}
      accessibilityHint={'Linked Accounts - Invite people to make payments on your behalf'}
      testID={testID ?? 'ProfileCell_profile.option.connections'}
      style={style}
    >
      <GlassCard borderRadius={REDESIGN_SPACING.quickActionRadius} padding={20}>
        <View style={styles.avatarStack}>
          {visibleAccounts.map((linkedAccount, index) => (
            <View
              key={linkedAccount.id}
              style={[
                styles.avatar,
                { backgroundColor: colors.activityIcon.bg, borderColor: colors.card.bg },
                { marginLeft: index > 0 ? -10 : 0, zIndex: visibleAccounts.length - index }
              ]}
            >
              {linkedAccount.user.photoUrl ? (
                <Image
                  source={{ uri: linkedAccount.user.photoUrl }}
                  style={styles.avatarImage}
                  resizeMode="cover"
                />
              ) : (
                <SVG_COMPONENTS.ADMIN width={20} height={20} color={colors.text.primary} />
              )}
            </View>
          ))}
          {hasMoreThanFour && (
            <View style={[styles.avatar, styles.plusAvatar, { backgroundColor: colors.text.dimmer, borderColor: colors.card.bg }]}>
              <SVG_COMPONENTS.ADD_BUTTON width={16} height={16} color={colors.text.primary} />
            </View>
          )}
        </View>

        <View style={styles.chevron}>
          <SVG_COMPONENTS.CHEVRON_RIGHT width={20} height={20} color={colors.text.dimmer} />
        </View>

        <Text style={[styles.title, { color: colors.text.tertiary }]}>{linkedAccountsStrings.cardTitle}</Text>
        <Text style={[styles.description, { color: colors.text.dim }]} numberOfLines={0}>
          {linkedAccountsStrings.cardDescription}
        </Text>
      </GlassCard>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  avatarStack: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 18,
  },
  plusAvatar: {},
  chevron: {
    position: 'absolute',
    top: 20,
    right: 20,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    marginBottom: 2,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.caption,
    paddingRight: 24,
  },
});
